import { uniq } from 'lodash';
import { computed } from 'vue';

import { SOURCE_STATUS_MAP } from '@/constants/data_sources';
import { MDM_TYPES } from '@/constants/mdm';
import { useFeatureFlagStore, useSourcesStore } from '@/stores';
import type { Source, SourceField } from '@/types/sources';

const [DELETING_STATUS] = SOURCE_STATUS_MAP.deleting;
const [UNAUTHORIZED_STATUS] = SOURCE_STATUS_MAP.missing;

const HAS_DISAPPEARED_REASON = 'has_disappeared';

type ColumnsByTableType = Record<string, string[]>;

export function useAccountProductSource(feedId: () => number | undefined) {
  const sourcesStore = useSourcesStore();
  const { hasFeatureFlag } = useFeatureFlagStore();

  const accountProductSource = computed<Source | undefined>(() => {
    const id = feedId();
    if (id == null) return undefined;
    return sourcesStore
      .getSourcesByFeedId(id)
      .find(
        (source) =>
          source.mdm_type === MDM_TYPES.ACCOUNT_PRODUCT &&
          source.status !== DELETING_STATUS,
      );
  });

  const hasSyncedAccountSource = computed<boolean>(() => {
    const id = feedId();
    if (id == null) return false;
    return sourcesStore
      .getSourcesByFeedId(id)
      .some(
        (source) =>
          source.mdm_type === MDM_TYPES.ACCOUNT &&
          source.status !== DELETING_STATUS &&
          source.status !== UNAUTHORIZED_STATUS,
      );
  });

  const fieldsByMdmProperty = computed<Record<string, SourceField>>(() => {
    const fields = accountProductSource.value?.fields ?? [];
    return Object.fromEntries(
      fields
        .filter((field) => Boolean(field.mdm_property))
        .map((field) => [field.mdm_property, field]),
    );
  });

  const lockedColumns = computed<string[]>(() =>
    Object.values(fieldsByMdmProperty.value).map((field) => field.column),
  );

  const disappearedFields = computed(() =>
    Object.entries(fieldsByMdmProperty.value).filter(
      ([, field]) => field.syncing_disabled_reason === HAS_DISAPPEARED_REASON,
    ),
  );

  const disappearedColumns = computed<string[]>(() =>
    disappearedFields.value.map(([, field]) => field.column),
  );

  const disappearedMdmProperties = computed<Set<string>>(
    () => new Set(disappearedFields.value.map(([mdmProperty]) => mdmProperty)),
  );

  /* The required-columns endpoint is keyed by integration type and cannot know
  about an object a user mapped, so the mapped columns are supplied here. A
  column the CRM no longer has is left out: the tree would check it, and the
  sync payload would then name a column that cannot sync. */
  function addRequiredColumns(requiredColumnsByTable: ColumnsByTableType) {
    const source = accountProductSource.value;
    if (!hasFeatureFlag('temp_account_products_sync') || !source)
      return requiredColumnsByTable;
    const syncable = lockedColumns.value.filter(
      (column) => !disappearedColumns.value.includes(column),
    );
    return {
      ...requiredColumnsByTable,
      [source.table]: uniq([
        ...(requiredColumnsByTable[source.table] ?? []),
        ...syncable,
      ]),
    };
  }

  return {
    accountProductSource,
    fieldsByMdmProperty,
    lockedColumns,
    disappearedColumns,
    addRequiredColumns,
    hasSyncedAccountSource,
    disappearedMdmProperties,
  };
}
